import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

import {
  useAnalytics,
  useInViewAnalytics,
} from '@service-initiation/shared-data-access-analytics';
import { useGoToNextStep } from '@service-initiation/shared-data-access-flow-manager';
import {
  LeftMainContentSection,
  MainContentContainer,
  PageContainer,
} from '@service-initiation/shared-ui-page-layout';
import { StepContentSkeleton } from '@service-initiation/shared-ui-step-content-skeleton';
import { TranslationEntries } from '@service-initiation/tech-care-data-access-i18n';
import {
  damageTypePageConfirmedAtom,
  store,
  useAvailableRepairsAsyncAtom,
  useDamageTypePageValidAsyncAtom,
  useIsOtherDeviceAsyncAtom,
  useRepairMessageAtom,
  useSelectedRepairTypeIdsAtom,
} from '@service-initiation/tech-care-data-access-store';
import { type Repair } from '@service-initiation/tech-care-data-access-tech-care-services';
import {
  NavigationButtonsContainer,
  type NavigationButtonsContainerProps,
} from '@service-initiation/tech-care-ui-navigation-buttons';
import { StepTitle } from '@service-initiation/tech-care-ui-step-title';

import { DamageForm, OtherDeviceForm } from './components';
import { addOrRemoveRepairType } from './utils';

const Container = styled.section``;

const TEXTS = TranslationEntries.Repairs.DamageType;
const OTHER_DEVICE_TEXTS = TranslationEntries.Repairs.OtherDevice;

export const DamageTypePage = () => {
  const { t } = useTranslation();
  const [, goToNextStep] = useGoToNextStep();

  const { loading: loadingAvailableDamages, value: damages = [] } =
    useAvailableRepairsAsyncAtom();

  const { loading, value: valid } = useDamageTypePageValidAsyncAtom();

  const { loading: loadingIsOtherDevice, value: isOtherDevice } =
    useIsOtherDeviceAsyncAtom();

  const [, setSelectedRepairTypes] = useSelectedRepairTypeIdsAtom();

  const [, setRepairMessage] = useRepairMessageAtom();

  const { ref: elementRef } = useInViewAnalytics('DamageType');

  const { dispatchClickEvent } = useAnalytics();

  const handleDamageSelection = useCallback(
    (damage: Repair) => {
      dispatchClickEvent('DamageSelected', {
        Cta: damage.repairType?.symptom,
      });
      setSelectedRepairTypes((prev) =>
        damage.repairType?.id
          ? addOrRemoveRepairType(prev, damage.repairType?.id)
          : prev
      );
    },
    [dispatchClickEvent, setSelectedRepairTypes]
  );

  const navigateButtonProps: NavigationButtonsContainerProps = {
    disableContinueButton: loading || !valid,
    onContinueButtonClick: () => {
      store.set(damageTypePageConfirmedAtom, true);
      goToNextStep();
    },
    pageName: 'DamageType',
    showBackButton: true,
  };

  return (
    <PageContainer>
      <MainContentContainer>
        <LeftMainContentSection>
          <Container data-testid="damage-type" id="DamageType" ref={elementRef}>
            {loadingAvailableDamages && loadingIsOtherDevice ? (
              <StepContentSkeleton />
            ) : (
              <div>
                <StepTitle
                  subtitle={
                    isOtherDevice
                      ? t(OTHER_DEVICE_TEXTS.SubTitle)
                      : t(TEXTS.SubTitle)
                  }
                  title={
                    isOtherDevice ? t(OTHER_DEVICE_TEXTS.Title) : t(TEXTS.Title)
                  }
                />
                {isOtherDevice ? (
                  <OtherDeviceForm handleTextAreaChange={setRepairMessage} />
                ) : (
                  <DamageForm
                    damages={damages}
                    handleDamageSelection={handleDamageSelection}
                    handleTextAreaChange={setRepairMessage}
                  />
                )}
              </div>
            )}
          </Container>
        </LeftMainContentSection>
        <NavigationButtonsContainer {...navigateButtonProps} />
      </MainContentContainer>
    </PageContainer>
  );
};
